// The duel's shared MOTION LANGUAGE — one easing set + one duration scale, read by BOTH renderers
// (the WebGL arena's JS tweens and the DOM motion layer's Web-Animations keyframes) so 2D and 3D move
// to the same beat. DESIGN.md law: "Motion = data. Flow/pulse/scan like a network, never bounce like a
// toy." Every curve here DECELERATES and never overshoots — no back/elastic anywhere in the duel.

/** Duration scale (ms). One ladder, used everywhere; no more scattered 130/160/220/440/700 magic. */
export const DUR = {
  quick: 140, // a snap (badge, glow ramp, small accent)
  base: 240, // the default beat (pop-in, flip, surge)
  travel: 380, // a card crossing the board (send-to-GY, draw, bounce)
  hold: 540, // a held flourish (summon bloom, attack lunge, dissolve)
} as const;

/** The physical attack beat, shared by the DOM field, WebGL diagnostic arena, playback pacing, and
 *  impact FX. It deliberately gives the card time to pull back and hold tension before the strike.
 *  Values are real milliseconds at duel speed ×1; consumers apply the user's speed multiplier. */
export const ATTACK_TIMING = {
  pullback: 280,
  windup: 150,
  lunge: 380,
  recoil: 340,
  impact: 810, // pullback + windup + lunge: exact contact frame
  total: 1150,
} as const;

// ── JS easings (the WebGL arena) ───────────────────────────────────────────────
/** Decelerate hard, settle exactly — the duel's default out-curve (replaces the old easeOutBack). */
export const easeOutExpo = (p: number): number => (p >= 1 ? 1 : 1 - Math.pow(2, -10 * p));
/** A gentler decelerate for in-place surges (lift, swell) where expo is too abrupt. */
export const easeOutQuint = (p: number): number => 1 - Math.pow(1 - p, 5);
/** Accelerate in — for a card leaving / de-rezzing. */
export const easeInQuint = (p: number): number => p * p * p * p * p;
/** Symmetric ease for spins/flips (in then out, no overshoot). */
export const easeInOutExpo = (p: number): number =>
  p === 0 ? 0 : p === 1 ? 1 : p < 0.5 ? Math.pow(2, 20 * p - 10) / 2 : (2 - Math.pow(2, -20 * p + 10)) / 2;
/** A single half-sine pulse 0→1→0 (impact flash, summon bloom envelope). */
export const pulse = (p: number): number => Math.sin(Math.min(1, Math.max(0, p)) * Math.PI);

// ── CSS equivalents (the DOM motion layer + Svelte CSS) ─────────────────────────
// Cubic-bezier approximations of the JS curves above, so a card's flight feels identical to a mesh's.
export const CSS_OUT_EXPO = "cubic-bezier(0.16, 1, 0.3, 1)";
export const CSS_OUT_QUINT = "cubic-bezier(0.22, 1, 0.36, 1)";
export const CSS_IN_QUINT = "cubic-bezier(0.64, 0, 0.78, 0)";

// ── the neon FX palette (no warm/orange "explosion" colours — cyberspace deletes, it doesn't burn) ──
export const FX = {
  cyan: "#19e0ff", // structure, your side, travel, draw
  magenta: "#ff2d7e", // danger / destruction / opponent — the "data-corruption" accent
  lime: "#9cff57", // special summon (rare tertiary)
  setBlue: "#5fa8ff", // a set card (dim cyan)
} as const;
